import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUsers, FaCheckCircle, FaTimesCircle, FaClock, FaPlaneDeparture,
  FaSync, FaBuilding, FaMapMarkerAlt, FaFingerprint, FaClipboardList,
  FaChartLine, FaFileAlt,
} from 'react-icons/fa';
import api, { errorMessage, getRole } from '../api';
import { MANAGEMENT, hasRole } from './attendance/roles';
import {
  PageShell, Card, StatCard, Badge, Button, Alert, Loading, RateBar, Table,
  EmptyState, formatDate, todayISO, ACCENT,
} from './attendance/ui';

/** Simple column chart for the 14-day attendance trend.
 *  Bars are absolutely positioned so their percentage heights resolve against
 *  the track rather than an auto-height flex parent. */
const TrendChart = ({ points }) => {
  if (!points?.length) return <EmptyState title="No trend data yet" />;
  const peak = Math.max(...points.map((point) => point.present), 1);
  return (
    <div className="flex items-stretch gap-1.5 h-56" role="img"
      aria-label="Attendance over the last 14 days">
      {points.map((point) => (
        <div key={point.date} className="flex-1 flex flex-col group">
          <div className="relative flex-1 border-b border-gray-100">
            <div
              className="absolute bottom-0 left-0 right-0 rounded-t-md transition-opacity group-hover:opacity-75"
              style={{
                height: `${Math.max((point.present / peak) * 100, 1.5)}%`,
                backgroundColor: ACCENT,
              }}
            />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 text-[10px] font-semibold text-gray-700 opacity-0 group-hover:opacity-100 whitespace-nowrap bg-white px-1.5 py-0.5 rounded shadow z-10">
              {point.present} present · {point.attendancePercentage}%
            </span>
          </div>
          <span className="h-10 pt-2 text-[10px] text-gray-500 whitespace-nowrap origin-top-left rotate-45">
            {point.label}
          </span>
        </div>
      ))}
    </div>
  );
};

/** Institution / department / location overview for supervisors and above. */
const ManagerDashboard = () => {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const role = getRole();
  const isManagement = hasRole(role, MANAGEMENT);

  const load = useCallback(async () => {
    setError('');
    try {
      const { data: payload } = await api.get('dashboard/', { params: { date } });
      setData(payload);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the dashboard.'));
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const syncDevices = async () => {
    setSyncing(true);
    setNotice('');
    try {
      const { data: result } = await api.post('biometrics/machines/sync-all/');
      setNotice(`Sync complete: ${result.processedLogs} punch(es) applied`
        + (result.unmatchedLogs ? `, ${result.unmatchedLogs} unmatched.` : '.'));
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Device sync failed.'));
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <PageShell title="Attendance Overview"><Loading /></PageShell>;

  const pieSegments = [
    { label: 'Present', value: Number(data?.present || 0), color: '#22c55e' },
    { label: 'Absent', value: Number(data?.absent || 0), color: '#ef4444' },
    { label: 'Late', value: Number(data?.late || 0), color: '#f59e0b' },
    { label: 'Leave', value: Number(data?.onLeave || 0), color: '#8b5cf6' },
  ].filter((segment) => segment.value > 0);

  const totalPie = pieSegments.reduce((sum, segment) => sum + segment.value, 0) || 1;
  const pieGradient = pieSegments.reduce((acc, segment, index) => {
    const start = pieSegments.slice(0, index).reduce((sum, item) => sum + item.value, 0) / totalPie * 100;
    const end = (pieSegments.slice(0, index + 1).reduce((sum, item) => sum + item.value, 0) / totalPie) * 100;
    return `${acc}${index > 0 ? ', ' : ''}${segment.color} ${start}% ${end}%`;
  }, '');

  return (
    <PageShell
      title="Attendance Overview"
      subtitle={formatDate(data?.date)}
      actions={
        <>
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)}
            className="px-3 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-[#15A857] dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" />
          {isManagement && (
            <Button variant="secondary" onClick={syncDevices} disabled={syncing}>
              <FaSync className={syncing ? 'animate-spin' : ''} /> Sync devices
            </Button>
          )}
          <Link to="/reports"><Button><FaFileAlt /> Reports</Button></Link>
        </>
      }
    >
      <Alert>{error}</Alert>
      <Alert tone="success">{notice}</Alert>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <StatCard icon={<FaUsers />} label="Total staff" tone="gray" value={data.totalStaff} />
        <StatCard icon={<FaCheckCircle />} label="Present" tone="green" value={data.present} />
        <StatCard icon={<FaTimesCircle />} label="Absent" tone="red" value={data.absent} />
        <StatCard icon={<FaClock />} label="Late" tone="amber" value={data.late} />
        <StatCard icon={<FaPlaneDeparture />} label="On leave" tone="purple" value={data.onLeave} />
        <StatCard icon={<FaChartLine />} label="Attendance" tone="blue"
          value={`${data.attendancePercentage}%`} hint={`${data.notRecorded} not recorded`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card title="Staff mix" className="lg:col-span-1">
          <div className="flex flex-col items-center justify-center gap-4">
            <div
              className="relative flex h-44 w-44 items-center justify-center rounded-full shadow-inner"
              style={{ background: `conic-gradient(${pieGradient})` }}
            >
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white text-center dark:bg-slate-800">
                <div>
                  <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{data.attendancePercentage}%</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Rate</div>
                </div>
              </div>
            </div>
            <div className="w-full space-y-2">
              {pieSegments.map((segment) => (
                <div key={segment.label} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/80">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: segment.color }} />
                    <span className="text-slate-600 dark:text-slate-300">{segment.label}</span>
                  </div>
                  <span className="font-semibold text-slate-800 dark:text-slate-100">{segment.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card title="Attendance trend · last 14 days" className="lg:col-span-2">
          <TrendChart points={data.trend} />
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="space-y-6">
          <Card title="Needs attention">
            <div className="space-y-3">
              <Link to="/leave-management"
                className="flex items-center justify-between p-3 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors">
                <span className="flex items-center gap-2 text-sm text-gray-700">
                  <FaPlaneDeparture className="text-amber-600" /> Pending leave requests
                </span>
                <span className="font-bold text-amber-700">{data.pendingLeaveRequests}</span>
              </Link>
              <Link to="/rural-submissions"
                className="flex items-center justify-between p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
                <span className="flex items-center gap-2 text-sm text-gray-700">
                  <FaClipboardList className="text-blue-600" /> Rural reports to review
                </span>
                <span className="font-bold text-blue-700">{data.pendingMonthlySubmissions}</span>
              </Link>
              {isManagement && (
                <Link to="/biometric-integration"
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <span className="flex items-center gap-2 text-sm text-gray-700">
                    <FaFingerprint className="text-gray-600" /> Unprocessed punches
                  </span>
                  <span className="font-bold text-gray-700">{data.devices?.unprocessedLogs ?? 0}</span>
                </Link>
              )}
            </div>
          </Card>

          {isManagement && (
            <Card title="Biometric devices">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-600">{data.devices?.online ?? 0}</div>
                  <div className="text-xs text-gray-500">Online</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-500">{data.devices?.offline ?? 0}</div>
                  <div className="text-xs text-gray-500">Offline</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-700">{data.devices?.total ?? 0}</div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card title={<span className="flex items-center gap-2"><FaBuilding className="text-gray-400" /> By department</span>}>
          <Table
            columns={['Department', 'Staff', 'Present', 'Attendance']}
            rows={data.byDepartment}
            empty="No departments configured."
            renderRow={(row) => (
              <tr key={row.name} className="border-b last:border-0">
                <td className="py-3 pr-4 font-medium text-gray-800">{row.name}</td>
                <td className="py-3 pr-4">{row.total}</td>
                <td className="py-3 pr-4">{row.present}</td>
                <td className="py-3 pr-4 w-40"><RateBar value={row.attendancePercentage} /></td>
              </tr>
            )}
          />
        </Card>

        <Card title={<span className="flex items-center gap-2"><FaMapMarkerAlt className="text-gray-400" /> By location</span>}>
          <Table
            columns={['Location', 'Staff', 'Present', 'Attendance']}
            rows={data.byLocation}
            empty="No locations configured."
            renderRow={(row) => (
              <tr key={row.name} className="border-b last:border-0">
                <td className="py-3 pr-4 font-medium text-gray-800">{row.name}</td>
                <td className="py-3 pr-4">{row.total}</td>
                <td className="py-3 pr-4">{row.present}</td>
                <td className="py-3 pr-4 w-40"><RateBar value={row.attendancePercentage} /></td>
              </tr>
            )}
          />
        </Card>
      </div>

      <Card title="Absent today">
        <Table
          columns={['Staff ID', 'Name', 'Department', 'Location', 'Status']}
          rows={data.recentAbsentees}
          empty="Nobody is marked absent for this date."
          renderRow={(row) => (
            <tr key={row.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="py-3 pr-4 font-mono text-xs text-gray-600">{row.employeeId}</td>
              <td className="py-3 pr-4 font-medium text-gray-800">{row.employeeName}</td>
              <td className="py-3 pr-4">{row.department || '—'}</td>
              <td className="py-3 pr-4">{row.location || '—'}</td>
              <td className="py-3 pr-4"><Badge value={row.status} /></td>
            </tr>
          )}
        />
      </Card>
    </PageShell>
  );
};

export default ManagerDashboard;
